import type { Root, SavedView } from "../../domain/app-types.ts";
import type { QuickNote, TaskTemplate } from "../../domain/entities.ts";
import { openDataManagementDialog } from "./controller.ts";
import { createArchive, csvToSpace, decryptArchive, encryptArchive, parseArchive, tasksToCsv, type PortableArchive, type PortableSpace } from "./transfer.ts";
import { markdownToSpace, spaceToMarkdown } from "./markdown-portability.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";

interface ConfirmationOptions { title: string; message: string; confirmLabel: string }

export interface DataManagementWorkflowCapabilities {
  activeRoot: () => string | null;
  activeRootName: () => string;
  canEdit: () => boolean;
  roots: () => Root[];
  savedViews: () => SavedView[];
  personalTemplates: () => TaskTemplate[];
  portableSpace: (rootId: string, progress?: ProgressReporter) => Promise<PortableSpace>;
  writingSpace?: (rootId: string) => Promise<PortableSpace>;
  portableQuickNotes: () => Promise<QuickNote[]>;
  importSpace: (space: PortableSpace, targetRootId: string, views: SavedView[], progress?: ProgressReporter) => Promise<void>;
  importPersonalTemplates: (templates: TaskTemplate[]) => Promise<void>;
  importQuickNotes: (notes: QuickNote[]) => Promise<void>;
  restoreArchive: (archive: PortableArchive, progress?: ProgressReporter) => Promise<void>;
  refreshAccess: () => Promise<void>;
  render: () => Promise<void>;
  sync: () => void;
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  download: (filename: string, contents: string, type: string) => void;
  today: () => string;
  icon: (name: string) => string;
  toast: (message: string, error?: boolean) => void;
}

export function safeExportFilename(value: string): string {
  return value.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "space";
}

function personalContentSummary(archive: PortableArchive): string {
  const parts = [archive.personalTemplates.length ? `${archive.personalTemplates.length} personal templates` : "", archive.quickNotes.length ? `${archive.quickNotes.length} private notes` : ""].filter(Boolean);
  return parts.length ? `${parts.join(" and ")} will also be added to your account. ` : "";
}

export function createDataManagementWorkflow(capabilities: DataManagementWorkflowCapabilities) {
  async function exportJson(progress?: ProgressReporter): Promise<string> {
    const rootId = capabilities.activeRoot();
    if (!rootId) throw new Error("Select a space before exporting.");
    const space = await capabilities.portableSpace(rootId, progress);
    const views = capabilities.savedViews().filter(view => view.rootId === rootId);
    capabilities.download(`${safeExportFilename(space.name)}-${capabilities.today()}.weirdo.json`, JSON.stringify(createArchive([space], views, capabilities.personalTemplates()), null, 2), "application/json");
    return "JSON export downloaded";
  }

  async function exportCsv(progress?: ProgressReporter): Promise<string> {
    const rootId = capabilities.activeRoot();
    if (!rootId) throw new Error("Select a space before exporting.");
    const space = await capabilities.portableSpace(rootId, progress);
    const names = new Map(space.categories.map(category => [category.id, category.name]));
    capabilities.download(`${safeExportFilename(space.name)}-${capabilities.today()}.csv`, tasksToCsv(space.tasks, id => names.get(id) || "", id => space.epics?.find(epic => epic.id === id)?.name || ""), "text/csv;charset=utf-8");
    return "CSV export downloaded";
  }

  async function importFile(file: File, progress?: ProgressReporter): Promise<string | null> {
    progress?.({ stage: "Reading import file" });
    const rootId = capabilities.activeRoot();
    if (!rootId || !capabilities.canEdit()) throw new Error("Select a space you can edit before importing.");
    const targetName = capabilities.activeRootName();
    const contents = await file.text();
    const isCsv = file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv";
    const isMarkdown = /\.(md|markdown)$/i.test(file.name) || file.type === "text/markdown";
    let archive: PortableArchive;
    try { archive = isMarkdown ? createArchive([markdownToSpace(contents, file.name.replace(/\.(md|markdown)$/i, ""))]) : isCsv ? createArchive([csvToSpace(contents, file.name.replace(/\.csv$/i, ""))]) : parseArchive(JSON.parse(contents)); }
    catch (error) { throw new Error(`The file could not be read. No content was imported. Choose a supported WeirDo JSON, CSV or Markdown file. ${error instanceof SyntaxError ? "The JSON is invalid." : error instanceof Error ? error.message : ""}`, { cause: error }); }
    const source = archive.spaces[0];
    if (!source) throw new Error("The export contains no spaces.");
    if (!await capabilities.confirm({ title: `Import into “${targetName}”?`, message: `${source.tasks.length} tasks and ${source.categories.length} collections will be added. Existing content will not be replaced. ${archive.spaces.length > 1 ? `Only the first of ${archive.spaces.length} spaces is imported here; use Restore backup for all spaces in an encrypted backup. ` : ""}${personalContentSummary(archive)}Assignees and sharing grants are not copied. ${isMarkdown ? "Markdown imports create plain open tasks and list collections, not full task metadata. " : ""}Anyone with access to the destination can read the imported space content.`, confirmLabel: "Import" })) return null;
    if (capabilities.activeRoot() !== rootId || !capabilities.canEdit()) throw new Error("The destination or your access changed. Open import again in the intended space.");
    try {
      await capabilities.importSpace(source, rootId, archive.savedViews as SavedView[], progress);
      progress?.({ stage: "Saving personal templates and notes" });
      await capabilities.importPersonalTemplates(archive.personalTemplates);
      await capabilities.importQuickNotes(archive.quickNotes);
    } catch (error) {
      throw new Error(`Import stopped. Some copies may already exist; inspect the destination before importing this file again to avoid duplicates. Keep the source file. ${error instanceof Error ? error.message : "Check your connection and access."}`, { cause: error });
    }
    capabilities.sync();
    progress?.({ stage: "Refreshing imported work" });
    try { await capabilities.render(); }
    catch { throw new Error("Import completed, but the page could not refresh. Reload to check it; do not import this file again."); }
    return `Imported ${source.tasks.length} tasks`;
  }

  async function createBackup(password: string, progress?: ProgressReporter): Promise<string> {
    if (password.length < 10) throw new Error("Use a backup password of at least 10 characters.");
    const roots = capabilities.roots().filter(root => root.role !== "viewer");
    const spaces: PortableSpace[] = [];
    for (const root of roots) {
      progress?.({ stage: "Collecting spaces", completed: spaces.length, total: roots.length });
      spaces.push(await capabilities.portableSpace(root.id, progress));
      progress?.({ stage: "Collecting spaces", completed: spaces.length, total: roots.length });
    }
    progress?.({ stage: "Collecting private notes" });
    const archive = createArchive(spaces, capabilities.savedViews(), capabilities.personalTemplates(), await capabilities.portableQuickNotes());
    progress?.({ stage: "Encrypting backup" });
    capabilities.download(`weirdo-backup-${capabilities.today()}.weirdo-backup`, await encryptArchive(archive, password), "application/json");
    return `Encrypted backup created for ${roots.length} spaces`;
  }

  async function restoreBackup(file: File, password: string, progress?: ProgressReporter): Promise<string | null> {
    progress?.({ stage: "Opening encrypted backup" });
    let archive: PortableArchive;
    try { archive = await decryptArchive(await file.text(), password); }
    catch (error) { throw new Error(`The backup could not be opened. Nothing was restored. ${error instanceof SyntaxError ? "Choose a valid encrypted .weirdo-backup file." : error instanceof Error ? error.message : "Check the file and password."}`, { cause: error }); }
    if (!await capabilities.confirm({ title: archive.spaces.length ? `Restore ${archive.spaces.length} ${archive.spaces.length === 1 ? "space" : "spaces"}?` : "Restore personal content?", message: `${archive.spaces.length ? `${archive.spaces.length} spaces will be restored as new copies.` : "No spaces will be created."} Existing spaces will not be changed. ${personalContentSummary(archive)}Sharing permissions are not restored. Repeating this operation may create duplicates.`, confirmLabel: "Restore backup" })) return null;
    try { await capabilities.restoreArchive(archive, progress); }
    catch (error) { throw new Error(`Restore stopped. Some copies may already exist; inspect your spaces before restoring again to avoid duplicates. Keep the backup file. ${error instanceof Error ? error.message : "Check your connection."}`, { cause: error }); }
    capabilities.sync();
    progress?.({ stage: "Refreshing restored work" });
    try { await capabilities.refreshAccess(); }
    catch { throw new Error("Backup restored, but access information could not refresh. Reload the page to check the restored spaces; do not restore the same backup again."); }
    try { await capabilities.render(); }
    catch { throw new Error("Backup restored, but the page could not refresh. Reload to check the restored spaces; do not restore this backup again."); }
    return archive.spaces.length ? `Restored ${archive.spaces.length} spaces` : "Personal content restored";
  }

  function open(): void {
    openDataManagementDialog({ hasActiveSpace: !!capabilities.activeRoot(), canEdit: capabilities.canEdit(), icon: capabilities.icon, toast: capabilities.toast, exportJson, exportCsv, exportMarkdown, importFile, createBackup, restoreBackup });
  }
  async function exportMarkdown(progress?: ProgressReporter): Promise<string> {
    progress?.({ stage: "Preparing readable export" });
    const rootId = capabilities.activeRoot(); if (!rootId) throw new Error("Select a space before exporting");
    const space = await (capabilities.writingSpace || capabilities.portableSpace)(rootId);
    capabilities.download(`${safeExportFilename(space.name)}-${capabilities.today()}.weirdo.md`, spaceToMarkdown(space), "text/markdown;charset=utf-8");
    return "Readable Markdown downloaded; use JSON for full task metadata and history";
  }

  return { open, exportJson, exportCsv, exportMarkdown, importFile, createBackup, restoreBackup };
}
